import { isFunction } from 'lodash';
import { ArrowRightIcon } from 'lucide-react';
import React from 'react';

import { Link } from '@/components/link';
import { Text } from '@/components/text';
import { SavedItemButton } from '@/components/ui/saved-item-button';
import { tv } from '@/utils/styles';

export interface DashboardListItemProps {
  title: string;
  description: string | React.ReactNode;
  href?: string;
  icon?: React.ReactNode;
  asColumn?: boolean;
  isSaved?: boolean;
  actionColumn?: React.ReactNode;
  onItemMouseOver?: () => void;
  onItemMouseLeave?: () => void;
  onItemClick?: () => void;
  onStarClick?: () => void;
}

const DashboardListItem = ({
  title,
  description,
  icon,
  href,
  asColumn,
  isSaved,
  actionColumn,
  onItemMouseOver,
  onItemMouseLeave,
  onItemClick,
  onStarClick,
}: DashboardListItemProps) => {
  const shouldShowStar = isFunction(onStarClick);
  const itemIsClickable = isFunction(onItemClick);

  const handleOnClick = () => {
    if (isFunction(onItemClick)) onItemClick();
  };

  const { base, content, titleText, descriptionText, row, arrowButton, listIcon } = styles({
    asColumn,
    isClickable: itemIsClickable,
  });

  return (
    <li className={base()}>
      {shouldShowStar && (
        <span className="ml-2">
          <SavedItemButton onClick={onStarClick} isSaved={isSaved} variant="small" />
        </span>
      )}
      <Link
        href={href || ''}
        type="button"
        title={title}
        onClick={handleOnClick}
        className={content()}
        onMouseOver={onItemMouseOver}
        onMouseLeave={onItemMouseLeave}
      >
        <div className={row()}>
          <div className="flex items-center">
            {icon && <span className={listIcon()}>{icon}</span>}
            <Text className={titleText()}>{title}</Text>
          </div>
          <Text className={descriptionText()}>{description}</Text>
        </div>
        <div className="flex items-center gap-4">
          {actionColumn || null}
          {itemIsClickable && (
            <div className={arrowButton()}>
              <ArrowRightIcon />
            </div>
          )}
        </div>
      </Link>
    </li>
  );
};

const styles = tv({
  slots: {
    base: 'group flex items-center rounded-lg leading-snug hover:bg-grey-100',
    content: 'flex flex-1 justify-between p-2',
    titleText: 'm-0 block text-base font-medium',
    descriptionText: 'm-0 block text-sm italic text-grey-500',
    arrowButton: 'grid size-8 place-items-center rounded bg-white [&_svg]:size-4',
    listIcon: 'mr-1.5 size-4 [&_svg]:size-4',
    row: '',
  },
  variants: {
    isClickable: {
      true: {
        content: 'cursor-pointer',
      },
      false: {
        content: 'pointer-events-none cursor-default',
      },
    },
    asColumn: {
      true: {
        row: 'md:flex md:w-full md:items-center md:justify-between',
      },
      false: {},
    },
  },
});

export { DashboardListItem };
